/* ---------------------------------------------------------------------------
   The same structure, in the other applications.

   A strip of links drawn at the top of every structure screen: the inspection
   record, the load rating, the programme, the site record — whichever of them is
   not the page you are already on.

   WHY IT IS ANCHORS AND NOT BUTTONS. Three of the four are other documents (see
   src/core/apps.ts), and a real `href` is what makes them behave like addresses:
   middle-click opens the programme in a second tab beside the record, the status
   bar shows where the link goes before it is pressed, and a copied link is the
   structure's programme rather than "whatever is selected". A button would have
   to reimplement all three and would get the first two wrong.

   WHAT IT DELIBERATELY DOES NOT SAY. Nothing about whether there is anything to
   find there — no "3 activities", no "rated". This build cannot know: the
   scheduler's baseline and the rating are per-structure reads that belong to
   the screen being linked to, and one of them is another application's store
   entirely. A count here would be either a fetch per structure or a guess, and
   the guess is the one this register does not make.
   --------------------------------------------------------------------------- */

import { h, icon, ICONS } from '../core/dom.ts';
import { APPS, appForShell, type AppDef } from '../core/apps.ts';
import { appHref, dashboardHref } from '../core/navigate.ts';
import { selectedPhase, type State } from '../core/store.ts';
import { accessLine, appAccess } from '../data/phase.ts';
import type { BridgeView } from '../data/types.ts';

/** What a link to this app means, once a structure is in hand. */
const FOR_STRUCTURE: Readonly<Record<string, string>> = {
  home: 'The register, summarised',
  structure: 'Bridge info, state and the reference tables',
  inspection: 'Condition, issues and the inventory sheet',
  rating: 'Load rating and posting',
  scheduler: 'The programme and the variance against it',
  monitoring: 'What has been built, and when',
};

/**
 * The other applications, for the structure in hand.
 *
 * Home is left out. It is where you go to pick a different structure, which is
 * the opposite of what this strip is for, and the rail in the nav pane already
 * carries it.
 */
export function appLinks(s: Readonly<State>, b: BridgeView): HTMLElement {
  const here = appForShell(s.shell);
  const others = APPS.filter((a) => a.id !== 'home' && a.id !== here.id);

  return h(
    'div',
    { class: 'applinks', 'aria-label': `${b.name} in the other applications` },
    h('span', { class: 'applinks__lead', text: 'This structure in' }),
    ...others.map((a) => appLink(s, a)),
    /* And every application's answer about it at once (LC-30). */
    h(
      'a',
      { class: 'applink', href: dashboardHref(s, b.id), title: `The dashboard of ${b.name}` },
      h('span', { class: 'applink__icon' }, icon(ICONS.chart, { size: 15 })),
      h('span', { text: 'Dashboard' }),
    ),
  );
}

function appLink(s: Readonly<State>, app: AppDef): HTMLElement {
  /* Hidden or read-only for this structure's state (LC-13): still a link, with
     the one line saying why as its title. */
  const p = selectedPhase(s);
  const mode = p === null || app.id === 'home' ? 'open' : appAccess(p, app.id).mode;
  const line = p === null || app.id === 'home' ? null : accessLine(p, app.id);
  return h(
    'a',
    {
      class: ['applink', mode !== 'open' && `applink--${mode}`],
      href: appHref(s, app.id),
      title: line ?? FOR_STRUCTURE[app.id] ?? app.blurb,
    },
    h('span', { class: 'applink__icon' }, icon(app.iconPath, { size: 15 })),
    h('span', { text: app.label }),
  );
}
